"use client";

import React, { useEffect, useMemo, useState } from "react";
import Image from "next/image";
import { Skeleton } from "antd";
import { ProductZoom } from "./product-zoom";
import LoadNormalModal from "../3DNormalModal";
import { useTranslations } from "next-intl";

const mainImageSize = 520;

export const getImagesForProduct = (product) => {
	if (!product?.metadata) return [];
	return JSON.parse(product.media);
};

function ImageGallery({ product }) {
	const imagesList = getImagesForProduct(product) as any[];
	const [active, setActive] = useState(0);
	const [mainImage, setMainImage] = useState<any>("");
	const [images, setImages] = useState<any[]>([]);
	const t = useTranslations();

	const changeProductImages = (product) => {
		if (product === null) {
			setImages([]);
			setMainImage("");
			setActive(0);
			return;
		}
		if (imagesList[0] && imagesList[0].url) {
			setMainImage(imagesList[0].url);
			setImages(imagesList);
		}
	};

	useEffect(() => {
		if (product) {
			changeProductImages(product);
			setActive(0);
		}
	}, [product]);

	const setActiveImgIndex = (index: number) => {
		if (index >= 0 && index < images.length) {
			setActive(index);
			setMainImage(images[index].url);
		}
	};

	const threeModel = useMemo<any>(() => {
		const json = product.metadata.find((item) => item.key === "threeModel");
		return json ? JSON.parse(json.value)[0] : null;
	}, [product]);

	const [threeDisplay, setThreeDisplay] = useState(true);
	const handleDisplay = () => {
		setThreeDisplay(!threeDisplay);
	};

	const thumbContainerRef = React.useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (thumbContainerRef.current && images.length > 0) {
			const activeThumb = thumbContainerRef.current.children[active] as HTMLElement;
			if (activeThumb) {
				activeThumb.scrollIntoView({
					behavior: "smooth",
					block: "nearest",
				});
			}
		}
	}, [active, images.length]);

	return (
		<div className="flex w-full select-none gap-3 max-md:flex-col-reverse md:items-stretch md:gap-4">
			{/* Thumbnail strip */}
			{images.length > 1 && (
				<div className="flex shrink-0 max-md:w-full md:w-[72px] lg:w-[80px]">
					<div
						ref={thumbContainerRef}
						className="flex gap-2 max-md:w-full max-md:overflow-x-auto max-md:pb-1 md:flex-col md:overflow-y-auto"
						style={{
							scrollbarWidth: "none",
							msOverflowStyle: "none",
						}}
					>
						{images.map((image: any, index: number) => (
							<button
								key={index}
								type="button"
								className={`group relative shrink-0 overflow-hidden rounded-md transition-all duration-200 max-md:h-[60px] max-md:w-[60px] md:aspect-square md:w-full ${
									active === index
										? "ring-2 ring-brand-ink ring-offset-1"
										: "ring-1 ring-zinc-200 hover:ring-zinc-400"
								}`}
								onMouseEnter={() => {
									setMainImage(image.url);
									setActive(index);
								}}
								onClick={() => {
									setMainImage(image.url);
									setActive(index);
								}}
							>
								<Image
									quality={80}
									src={image.url}
									width={80}
									height={80}
									alt={`Product view ${index + 1}`}
									className="h-full w-full object-cover"
								/>
							</button>
						))}
					</div>
				</div>
			)}

			{/* Main display area */}
			<div className="relative flex flex-1 items-center justify-center overflow-hidden rounded-lg bg-[oklch(97.5%_0.005_72)] max-md:aspect-square md:aspect-square">
				{/* 3D toggle */}
				{threeModel && (
					<div className="absolute right-2.5 top-2.5 z-20">
						<button
							className="tooltip-icon group !flex h-9 w-9 !items-center !justify-center rounded-full bg-white/90 shadow-sm backdrop-blur-sm transition-colors hover:bg-brand-ink"
							onClick={handleDisplay}
						>
							<i className="ri-box-3-line text-[20px] text-brand-ink transition-colors group-hover:text-white" />
							<span className="tooltip-text">{t("product.toggle-3d-image")}</span>
						</button>
					</div>
				)}

				{/* 3D model layer */}
				<div
					className={`absolute inset-0 rounded-lg transition duration-300 ease-in-out ${
						threeDisplay ? "invisible opacity-0" : "visible opacity-100"
					}`}
				>
					{threeModel && (
						<LoadNormalModal
							modalUrl={process.env.NEXT_PUBLIC_LANGUAGE_LIST_URL + `/medias/file_download/` + threeModel.id}
							className="w-full"
							fileName={threeModel.name}
							metalness={threeModel.metalness}
							color={threeModel.color}
							roughness={threeModel.roughness}
						/>
					)}
				</div>

				{/* Image layer */}
				<div
					className={`z-10 w-full transition duration-300 ease-in-out ${
						!threeDisplay ? "invisible opacity-0" : "visible opacity-100"
					}`}
				>
					{mainImage ? (
						<ProductZoom
							src={mainImage}
							imageWidth={mainImageSize}
							imageHeight={mainImageSize}
							setActiveImgIndex={setActiveImgIndex}
							quality={100}
							imgList={images}
							fit="contain"
						/>
					) : (
						<div className="flex aspect-square w-full items-center justify-center">
							<Skeleton.Image active className="!h-48 !w-48" />
						</div>
					)}
				</div>

				{/* Image counter */}
				{images.length > 1 && threeDisplay && (
					<span className="absolute bottom-3 right-3 z-20 rounded-full bg-brand-ink/70 px-2.5 py-0.5 font-body text-[11px] tabular-nums text-white backdrop-blur-sm">
						{active + 1} / {images.length}
					</span>
				)}
			</div>
		</div>
	);
}

export default React.memo(ImageGallery);
